/**
 * NotificationEventsAdmin — Admin-UI fuer das Notification-Center (#898).
 *
 * Tabs:
 * - Config: Feature-Flag, Quiet-Hours, Digest-Default, Admin-Override-OFF
 * - Events: Registry-Liste mit Filter + Customize-Modal (Template-Editor + Preview + Pflichtvariablen-Check)
 * - Log: Dispatch-Audit paginiert (filter nach event_key/user/kanal/status)
 *
 * @page /admin/notification-events
 * @roles admin, administrator
 * @i18n notificationsAdmin.*
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { ToggleSwitch } from '../components/ui';

const API = import.meta.env.VITE_API_URL || '';

const CHANNELS = ['in_app', 'email', 'sms', 'telegram'];

// Pflichtvariablen werden aus template_body + template_subject per Regex extrahiert.
function extractVars(s) {
 if (!s) return [];
 const set = new Set();
 const re = /\{\{([a-zA-Z0-9_.]+)\}\}/g;
 let m;
 while ((m = re.exec(s))) set.add(m[1]);
 const condRe = /\{\{#([a-zA-Z0-9_.]+)\}\}/g;
 while ((m = condRe.exec(s))) set.add(m[1]);
 return Array.from(set);
}

function renderPreview(tpl, data) {
 if (!tpl) return '';
 return tpl
 .replace(/\{\{#([a-zA-Z0-9_.]+)\}\}([\s\S]*?)\{\{\/\1\}\}/g, (_, key, inner) => (data[key] ? inner : ''))
 .replace(/\{\{([a-zA-Z0-9_.]+)\}\}/g, (_, key) => (data[key] !== undefined && data[key] !== null ? String(data[key]) : `{{${key}}}`));
}

export default function NotificationEventsAdmin() {
 const { t } = useLanguage();
 const { user } = useAuth();
 const [tab, setTab] = useTabParam('config');

 const [config, setConfig] = useState(null);
 const [configSaving, setConfigSaving] = useState(false);

 const [events, setEvents] = useState([]);
 const [eventsLoading, setEventsLoading] = useState(false);
 const [filter, setFilter] = useState('');
 const [modulFilter, setModulFilter] = useState('all');
 const [editEvent, setEditEvent] = useState(null); // open edit modal

 const [log, setLog] = useState([]);
 const [logLoading, setLogLoading] = useState(false);
 const [logFilter, setLogFilter] = useState({ event_key: '', user_id: '', kanal: '', status: '' });

 const [msg, setMsg] = useState('');
 const [err, setErr] = useState('');

 const loadConfig = useCallback(() => {
 fetch(`${API}/api/notifications/admin/config`, { credentials: 'include' })
 .then(r => r.json()).then(d => setConfig(d.config || {})).catch(() => {});
 }, []);

 const loadEvents = useCallback(() => {
 setEventsLoading(true);
 fetch(`${API}/api/notifications/admin/events`, { credentials: 'include' })
 .then(r => r.json()).then(d => setEvents(d.events || [])).catch(() => {}).finally(() => setEventsLoading(false));
 }, []);

 const loadLog = useCallback(() => {
 setLogLoading(true);
 const q = new URLSearchParams();
 Object.entries(logFilter).forEach(([k, v]) => { if (v) q.set(k, v); });
 q.set('limit', '150');
 fetch(`${API}/api/notifications/admin/log?${q}`, { credentials: 'include' })
 .then(r => r.json()).then(d => setLog(d.log || [])).catch(() => {}).finally(() => setLogLoading(false));
 }, [logFilter]);

 useEffect(() => { if (tab === 'config') loadConfig(); }, [tab, loadConfig]);
 useEffect(() => { if (tab === 'events') loadEvents(); }, [tab, loadEvents]);
 useEffect(() => { if (tab === 'log') loadLog(); }, [tab, loadLog]);

 const saveConfig = async (patch) => {
 setConfigSaving(true);
 setMsg(''); setErr('');
 try {
 const r = await fetch(`${API}/api/notifications/admin/config`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(patch),
 });
 if (!r.ok) throw new Error(await r.text());
 setMsg(t('notificationsAdmin.saved'));
 setTimeout(() => setMsg(''), 2500);
 setConfig({ ...config, ...Object.fromEntries(Object.entries(patch).map(([k, v]) => [k, JSON.stringify(v)])) });
 } catch (e) {
 setErr(e.message || t('common.error'));
 } finally {
 setConfigSaving(false);
 }
 };

 const saveEvent = async (eventKey, patch) => {
 try {
 const r = await fetch(`${API}/api/notifications/admin/events/${encodeURIComponent(eventKey)}`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(patch),
 });
 if (!r.ok) throw new Error(await r.text());
 const { event } = await r.json();
 setEvents(events.map(e => e.event_key === eventKey ? event : e));
 return event;
 } catch (e) {
 setErr(e.message || t('common.error'));
 throw e;
 }
 };

 const sendTest = async (eventKey, data, channels) => {
 try {
 const r = await fetch(`${API}/api/notifications/admin/test`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ event_key: eventKey, data, channels }),
 });
 const j = await r.json();
 if (!r.ok) throw new Error(j.error || t('common.error'));
 return j.result;
 } catch (e) {
 setErr(e.message || t('common.error'));
 throw e;
 }
 };

 const module_list = useMemo(() => {
 const set = new Set(events.map(e => e.modul));
 return ['all', ...Array.from(set).sort()];
 }, [events]);

 const filtered_events = useMemo(() => {
 return events.filter(ev => {
 if (modulFilter !== 'all' && ev.modul !== modulFilter) return false;
 if (!filter) return true;
 const f = filter.toLowerCase();
 return ev.event_key.toLowerCase().includes(f) || (ev.label_de || '').toLowerCase().includes(f) || (ev.label_en || '').toLowerCase().includes(f);
 });
 }, [events, filter, modulFilter]);

 const isAdmin = user && ['admin', 'administrator'].includes(user.rolle);
 if (!isAdmin) {
 return <div className="p-6"><div className="text-sm text-[var(--text-secondary)]">{t('common.noAccess')}</div></div>;
 }

 const card = "bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-6";
 const tabBtn = (active) => `px-3.5 py-1.5 rounded-[8px] text-[11px] font-medium transition-colors ${active ? 'bg-white text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-secondary)]'}`;
 const inp = "bg-[var(--bg-input)] border border-[var(--border-input)] rounded-[10px] px-3 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]";

 return (
 <div className="p-6 max-w-[1400px] mx-auto space-y-5">
 <div className="flex items-start justify-between gap-4 flex-wrap">
 <div>
 <h1 className="text-xl font-semibold text-[var(--text-primary)]">{t('notificationsAdmin.title')}</h1>
 <p className="text-xs text-[var(--text-secondary)] mt-1">{t('notificationsAdmin.subtitle')}</p>
 </div>
 <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card)] flex">
 <button onClick={() => setTab('config')} className={tabBtn(tab === 'config')}>{t('notificationsAdmin.tabConfig')}</button>
 <button onClick={() => setTab('events')} className={tabBtn(tab === 'events')}>{t('notificationsAdmin.tabEvents')}</button>
 <button onClick={() => setTab('log')} className={tabBtn(tab === 'log')}>{t('notificationsAdmin.tabLog')}</button>
 </div>
 </div>

 {msg && <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 text-xs text-emerald-500">{msg}</div>}
 {err && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-400">{err} <button onClick={() => setErr('')} className="ml-2 opacity-60 hover:opacity-100">×</button></div>}

 {/* ═══ CONFIG TAB ═══ */}
 {tab === 'config' && config && (
 <div className={card}>
 <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('notificationsAdmin.globalConfig')}</h3>
 <div className="space-y-5">
 <div className="flex items-center justify-between py-3 border-b border-[var(--border-card)]/50">
 <div>
 <div className="text-xs font-medium text-[var(--text-primary)]">{t('notificationsAdmin.featureFlag')}</div>
 <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t('notificationsAdmin.featureFlagDesc')}</div>
 </div>
 <ToggleSwitch size="sm" checked={config.notification_center_enabled === true} onChange={v => saveConfig({ notification_center_enabled: v })} disabled={configSaving} />
 </div>

 <div className="flex items-center justify-between py-3 border-b border-[var(--border-card)]/50">
 <div>
 <div className="text-xs font-medium text-[var(--text-primary)]">{t('notificationsAdmin.adminOverride')}</div>
 <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t('notificationsAdmin.adminOverrideDesc')}</div>
 </div>
 <ToggleSwitch size="sm" checked={config.notification_admin_override_off === true} onChange={v => saveConfig({ notification_admin_override_off: v })} disabled={configSaving} />
 </div>

 <div className="grid grid-cols-[1fr,140px] gap-4 items-center py-3 border-b border-[var(--border-card)]/50">
 <div>
 <div className="text-xs font-medium text-[var(--text-primary)]">{t('notificationsAdmin.quietHours')}</div>
 <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t('notificationsAdmin.quietHoursDesc')}</div>
 </div>
 <div className="flex items-center gap-2">
 <input type="time" defaultValue={config.notification_quiet_hours_start || '22:00'} onBlur={e => saveConfig({ notification_quiet_hours_start: e.target.value })} className={inp} />
 <span className="text-[10px] text-[var(--text-tertiary)]">–</span>
 <input type="time" defaultValue={config.notification_quiet_hours_end || '06:00'} onBlur={e => saveConfig({ notification_quiet_hours_end: e.target.value })} className={inp} />
 </div>
 </div>

 <div className="grid grid-cols-[1fr,140px] gap-4 items-center py-3">
 <div>
 <div className="text-xs font-medium text-[var(--text-primary)]">{t('notificationsAdmin.digestDefault')}</div>
 <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t('notificationsAdmin.digestDefaultDesc')}</div>
 </div>
 <select defaultValue={config.notification_digest_default || 'sofort'} onChange={e => saveConfig({ notification_digest_default: e.target.value })} className={inp}>
 <option value="sofort">sofort</option>
 <option value="taeglich">taeglich</option>
 <option value="woechentlich">woechentlich</option>
 </select>
 </div>
 </div>
 </div>
 )}

 {/* ═══ EVENTS TAB ═══ */}
 {tab === 'events' && (
 <div className={card}>
 <div className="flex items-center gap-3 mb-4 flex-wrap">
 <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
 placeholder={t('notificationsAdmin.filter')}
 className={`${inp} flex-1 min-w-[200px]`} />
 <select value={modulFilter} onChange={e => setModulFilter(e.target.value)} className={inp}>
 {module_list.map(m => <option key={m} value={m}>{m === 'all' ? t('common.all') : m}</option>)}
 </select>
 <span className="text-[11px] text-[var(--text-tertiary)]">{filtered_events.length} / {events.length}</span>
 </div>

 {eventsLoading ? (
 <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full text-xs">
 <thead className="border-b border-[var(--border-card)]">
 <tr className="text-left">
 <th className="py-2 px-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('notificationsAdmin.eventKey')}</th>
 <th className="py-2 px-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('notificationsAdmin.labelDe')}</th>
 <th className="py-2 px-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('notificationsAdmin.modul')}</th>
 <th className="py-2 px-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider text-center">{t('notificationsAdmin.critical')}</th>
 <th className="py-2 px-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider text-center">{t('notificationsAdmin.active')}</th>
 <th className="py-2 px-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider text-center">{t('notificationsAdmin.customized')}</th>
 <th className="py-2 px-2 text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider"></th>
 </tr>
 </thead>
 <tbody>
 {filtered_events.map(ev => (
 <tr key={ev.event_key} className="border-b border-[var(--border-card)]/50 hover:bg-[var(--bg-inset)]/50">
 <td className="py-2 px-2 font-mono text-[11px] text-[var(--text-primary)]">{ev.event_key}</td>
 <td className="py-2 px-2 text-[var(--text-primary)]">{ev.label_de}</td>
 <td className="py-2 px-2"><span className="text-[10px] px-2 py-0.5 rounded bg-[var(--bg-inset)] text-[var(--text-secondary)] font-medium">{ev.modul}</span></td>
 <td className="py-2 px-2 text-center">{ev.kritisch ? <span className="text-red-500">●</span> : <span className="text-[var(--text-tertiary)]">○</span>}</td>
 <td className="py-2 px-2 text-center">{ev.aktiv ? <span className="text-emerald-500">●</span> : <span className="text-[var(--text-tertiary)]">○</span>}</td>
 <td className="py-2 px-2 text-center">{ev.customized_at ? <span className="text-blue-500">●</span> : <span className="text-[var(--text-tertiary)]">○</span>}</td>
 <td className="py-2 px-2 text-right">
 <button onClick={() => setEditEvent(ev)} className="text-[#338edc] hover:underline text-[11px]">{t('notificationsAdmin.edit')}</button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>
 )}

 {/* ═══ LOG TAB ═══ */}
 {tab === 'log' && (
 <div className={card}>
 <div className="grid grid-cols-[1fr,1fr,140px,140px,auto] gap-2 mb-4">
 <input type="text" placeholder="event_key" value={logFilter.event_key} onChange={e => setLogFilter({ ...logFilter, event_key: e.target.value })} className={inp} />
 <input type="text" placeholder="user_id" value={logFilter.user_id} onChange={e => setLogFilter({ ...logFilter, user_id: e.target.value })} className={inp} />
 <select value={logFilter.kanal} onChange={e => setLogFilter({ ...logFilter, kanal: e.target.value })} className={inp}>
 <option value="">{t('notificationsAdmin.channelAll')}</option>
 {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
 </select>
 <select value={logFilter.status} onChange={e => setLogFilter({ ...logFilter, status: e.target.value })} className={inp}>
 <option value="">{t('notificationsAdmin.statusAll')}</option>
 <option value="sent">sent</option>
 <option value="queued">queued</option>
 <option value="blocked">blocked</option>
 <option value="failed">failed</option>
 <option value="skipped">skipped</option>
 </select>
 <button onClick={loadLog} className="bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-semibold px-4 py-1.5 rounded-[10px]">{t('common.refresh')}</button>
 </div>

 {logLoading ? (
 <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full text-xs">
 <thead className="border-b border-[var(--border-card)]">
 <tr className="text-left">
 <th className="py-2 px-2 text-[10px] font-semibold uppercase text-[var(--text-secondary)]">{t('notificationsAdmin.logTime')}</th>
 <th className="py-2 px-2 text-[10px] font-semibold uppercase text-[var(--text-secondary)]">{t('notificationsAdmin.logEvent')}</th>
 <th className="py-2 px-2 text-[10px] font-semibold uppercase text-[var(--text-secondary)]">{t('notificationsAdmin.logUser')}</th>
 <th className="py-2 px-2 text-[10px] font-semibold uppercase text-[var(--text-secondary)]">{t('notificationsAdmin.logChannel')}</th>
 <th className="py-2 px-2 text-[10px] font-semibold uppercase text-[var(--text-secondary)]">{t('notificationsAdmin.logStatus')}</th>
 <th className="py-2 px-2 text-[10px] font-semibold uppercase text-[var(--text-secondary)]">{t('notificationsAdmin.logSubject')}</th>
 </tr>
 </thead>
 <tbody>
 {log.map(l => (
 <tr key={l.id} className="border-b border-[var(--border-card)]/50">
 <td className="py-2 px-2 text-[var(--text-tertiary)] whitespace-nowrap">{new Date(l.created_at).toLocaleString('de-AT', { hour12: false })}</td>
 <td className="py-2 px-2 font-mono text-[11px]">{l.event_key}</td>
 <td className="py-2 px-2">{l.user_id ?? (l.ma_id ? `ma:${l.ma_id}` : '—')}</td>
 <td className="py-2 px-2">{l.kanal}</td>
 <td className="py-2 px-2">
 <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
 l.status === 'sent' ? 'bg-emerald-500/10 text-emerald-500' :
 l.status === 'queued' ? 'bg-amber-500/10 text-amber-500' :
 l.status === 'blocked' || l.status === 'failed' ? 'bg-red-500/10 text-red-500' :
 'bg-[var(--bg-inset)] text-[var(--text-secondary)]'
 }`}>{l.status}</span>
 </td>
 <td className="py-2 px-2 text-[var(--text-secondary)] truncate max-w-[400px]">{l.rendered_subject || l.grund || ''}</td>
 </tr>
 ))}
 {log.length === 0 && (
 <tr><td colSpan={6} className="py-6 text-center text-[var(--text-tertiary)] text-[11px]">{t('notificationsAdmin.logEmpty')}</td></tr>
 )}
 </tbody>
 </table>
 </div>
 )}
 </div>
 )}

 {/* ═══ EDIT-EVENT MODAL ═══ */}
 {editEvent && <EditEventModal event={editEvent} onClose={() => setEditEvent(null)} onSave={saveEvent} onTest={sendTest} />}
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════════
// EDIT-EVENT MODAL — Template-Editor + Preview + Pflichtvariablen-Check
// ═══════════════════════════════════════════════════════════════════
function EditEventModal({ event, onClose, onSave, onTest }) {
 const { t } = useLanguage();
 const [form, setForm] = useState({
 label_de: event.label_de || '',
 label_en: event.label_en || '',
 beschreibung: event.beschreibung || '',
 kritisch: event.kritisch,
 aktiv: event.aktiv,
 template_subject: event.template_subject || '',
 template_body: event.template_body || '',
 template_sms: event.template_sms || '',
 template_inapp_titel: event.template_inapp_titel || '',
 template_inapp_nachricht: event.template_inapp_nachricht || '',
 link_pattern: event.link_pattern || '',
 });
 const [saving, setSaving] = useState(false);
 const [previewData, setPreviewData] = useState({});
 const [showPreview, setShowPreview] = useState(false);

 // Pflichtvariablen: alle {{var}} die in subject/body/sms/inapp_titel/inapp_nachricht vorkommen.
 const requiredVars = useMemo(() => {
 const all = new Set();
 ['template_subject', 'template_body', 'template_sms', 'template_inapp_titel', 'template_inapp_nachricht', 'link_pattern']
 .forEach(k => extractVars(form[k]).forEach(v => all.add(v)));
 return Array.from(all).sort();
 }, [form]);

 // Fuer Preview-Defaults
 useEffect(() => {
 const next = {};
 requiredVars.forEach(v => { next[v] = previewData[v] ?? `[${v}]`; });
 setPreviewData(next);
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [requiredVars.join(',')]);

 const save = async () => {
 setSaving(true);
 try {
 await onSave(event.event_key, form);
 onClose();
 } catch {
 /* Fehler wird im Parent angezeigt */
 } finally { setSaving(false); }
 };

 const test = async () => {
 setSaving(true);
 try {
 await onTest(event.event_key, previewData, null);
 alert(t('notificationsAdmin.testSent'));
 } catch (e) {
 alert(t('notificationsAdmin.testFailed') + ': ' + e.message);
 } finally { setSaving(false); }
 };

 const inp = "w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-[10px] px-3 py-2 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]";
 const lbl = "block text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1";
 const btnPrimary = "bg-[#338edc] hover:bg-[#2b7bc4] disabled:opacity-50 text-white text-xs font-semibold px-5 py-2 rounded-[10px]";
 const btnSecondary = "border border-[var(--border-input)] hover:border-[var(--border-input-focus)] text-[var(--text-primary)] text-xs font-medium px-4 py-2 rounded-[10px]";

 return (
 <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl max-w-[1100px] w-full max-h-[92vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
 <div className="flex items-start justify-between mb-5">
 <div>
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('notificationsAdmin.editEvent')}</h3>
 <div className="font-mono text-[11px] text-[var(--text-tertiary)] mt-0.5">{event.event_key}</div>
 </div>
 <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] text-lg leading-none">×</button>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
 {/* LEFT — Form */}
 <div className="space-y-4">
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className={lbl}>{t('notificationsAdmin.labelDe')}</label>
 <input className={inp} value={form.label_de} onChange={e => setForm({ ...form, label_de: e.target.value })} />
 </div>
 <div>
 <label className={lbl}>{t('notificationsAdmin.labelEn')}</label>
 <input className={inp} value={form.label_en} onChange={e => setForm({ ...form, label_en: e.target.value })} />
 </div>
 </div>
 <div>
 <label className={lbl}>{t('notificationsAdmin.description')}</label>
 <input className={inp} value={form.beschreibung} onChange={e => setForm({ ...form, beschreibung: e.target.value })} />
 </div>

 <div className="flex items-center gap-5 py-2">
 <label className="flex items-center gap-2 text-xs text-[var(--text-primary)]">
 <ToggleSwitch size="sm" checked={form.aktiv} onChange={v => setForm({ ...form, aktiv: v })} />
 {t('notificationsAdmin.active')}
 </label>
 <label className="flex items-center gap-2 text-xs text-[var(--text-primary)]">
 <ToggleSwitch size="sm" checked={form.kritisch} onChange={v => setForm({ ...form, kritisch: v })} />
 {t('notificationsAdmin.critical')}
 </label>
 </div>

 <div>
 <label className={lbl}>{t('notificationsAdmin.inAppTitle')}</label>
 <input className={inp} value={form.template_inapp_titel} onChange={e => setForm({ ...form, template_inapp_titel: e.target.value })} placeholder={t('notificationsAdmin.inAppTitlePlaceholder')} />
 </div>
 <div>
 <label className={lbl}>{t('notificationsAdmin.inAppMessage')}</label>
 <textarea className={inp} rows={2} value={form.template_inapp_nachricht} onChange={e => setForm({ ...form, template_inapp_nachricht: e.target.value })} />
 </div>

 <div>
 <label className={lbl}>{t('notificationsAdmin.emailSubject')}</label>
 <input className={inp} value={form.template_subject} onChange={e => setForm({ ...form, template_subject: e.target.value })} />
 </div>
 <div>
 <label className={lbl}>{t('notificationsAdmin.emailBody')}</label>
 <textarea className={`${inp} font-mono`} rows={8} value={form.template_body} onChange={e => setForm({ ...form, template_body: e.target.value })} />
 </div>

 <div>
 <label className={lbl}>{t('notificationsAdmin.smsLabel')}</label>
 <textarea className={inp} rows={2} maxLength={500} value={form.template_sms} onChange={e => setForm({ ...form, template_sms: e.target.value })} />
 <div className="text-[10px] text-[var(--text-tertiary)] mt-1">{form.template_sms.length}/500</div>
 </div>

 <div>
 <label className={lbl}>{t('notificationsAdmin.linkPattern')}</label>
 <input className={inp} value={form.link_pattern} onChange={e => setForm({ ...form, link_pattern: e.target.value })} placeholder={t('notificationsAdmin.linkPatternPlaceholder')} />
 </div>
 </div>

 {/* RIGHT — Pflichtvariablen + Preview */}
 <div className="space-y-4">
 <div className="border border-[var(--border-card)] rounded-2xl p-4">
 <h4 className="text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('notificationsAdmin.requiredVars')}</h4>
 {requiredVars.length === 0 ? (
 <div className="text-[11px] text-[var(--text-tertiary)]">{t('notificationsAdmin.noVars')}</div>
 ) : (
 <div className="space-y-2">
 {requiredVars.map(v => (
 <div key={v} className="grid grid-cols-[120px,1fr] gap-2 items-center">
 <label className="text-[11px] font-mono text-[var(--text-primary)]">{`{{${v}}}`}</label>
 <input className={inp} value={previewData[v] ?? ''} onChange={e => setPreviewData({ ...previewData, [v]: e.target.value })} />
 </div>
 ))}
 </div>
 )}
 </div>

 <div className="border border-[var(--border-card)] rounded-2xl p-4">
 <h4 className="text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('notificationsAdmin.preview')}</h4>
 <div className="space-y-3 text-[11px]">
 <div>
 <div className="text-[10px] text-[var(--text-tertiary)] mb-0.5">{t('notificationsAdmin.previewInAppTitle')}</div>
 <div className="text-[var(--text-primary)] font-medium">{renderPreview(form.template_inapp_titel, previewData) || <span className="text-[var(--text-tertiary)]">—</span>}</div>
 </div>
 <div>
 <div className="text-[10px] text-[var(--text-tertiary)] mb-0.5">{t('notificationsAdmin.previewInAppMessage')}</div>
 <div className="text-[var(--text-primary)]">{renderPreview(form.template_inapp_nachricht, previewData) || <span className="text-[var(--text-tertiary)]">—</span>}</div>
 </div>
 <div>
 <div className="text-[10px] text-[var(--text-tertiary)] mb-0.5">{t('notificationsAdmin.previewEmailSubject')}</div>
 <div className="text-[var(--text-primary)] font-medium">{renderPreview(form.template_subject, previewData) || <span className="text-[var(--text-tertiary)]">—</span>}</div>
 </div>
 <div>
 <div className="text-[10px] text-[var(--text-tertiary)] mb-0.5">{t('notificationsAdmin.previewEmailBody')}</div>
 <div className="text-[var(--text-primary)] bg-[var(--bg-inset)] border border-[var(--border-card)] rounded p-2 max-h-[160px] overflow-y-auto" dangerouslySetInnerHTML={{ __html: renderPreview(form.template_body, previewData) || '<span style="color:#888">—</span>' }} />
 </div>
 <div>
 <div className="text-[10px] text-[var(--text-tertiary)] mb-0.5">{t('notificationsAdmin.previewSms')}</div>
 <div className="text-[var(--text-primary)]">{renderPreview(form.template_sms, previewData) || <span className="text-[var(--text-tertiary)]">—</span>}</div>
 </div>
 <div>
 <div className="text-[10px] text-[var(--text-tertiary)] mb-0.5">{t('notificationsAdmin.previewLink')}</div>
 <div className="text-[#338edc] font-mono">{renderPreview(form.link_pattern, previewData) || <span className="text-[var(--text-tertiary)]">—</span>}</div>
 </div>
 </div>
 </div>
 </div>
 </div>

 <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-[var(--border-card)]">
 <button onClick={test} disabled={saving} className={btnSecondary}>{t('notificationsAdmin.sendTest')}</button>
 <button onClick={onClose} disabled={saving} className={btnSecondary}>{t('common.cancel')}</button>
 <button onClick={save} disabled={saving} className={btnPrimary}>{saving ? '…' : t('common.save')}</button>
 </div>
 </div>
 </div>
 );
}
